import { create } from 'zustand'
import axios from '../config/axios'

export const useSearchStore = create((set, get) => ({
  results: [],
  processes: [], // [{ name, manufacturers[] }]
  selectedProcess: null,
  manufacturers: [], // [{ name, series[] }] — filtered by selected process
  selectedSeries: [],
  loading: false,
  error: null,
  searchConfig: null,
  searchedDimensions: null, // { length, width, depth } of the last search

  // Fetch processes and their manufacturers/series
  fetchSeries: async () => {
    try {
      const response = await axios.get('/api/search/series')
      const allProcesses = response.data.processes ?? []
      const { user, isAdmin } = (await import('./authStore')).useAuthStore.getState()
      const allowed = user?.allowedManufacturers || []

      // Filter processes to only include allowed manufacturers
      const filtered = allProcesses.map(proc => ({
        ...proc,
        manufacturers: proc.manufacturers.filter(m =>
          isAdmin || allowed.includes('*') || allowed.length === 0 || allowed.includes(m.name)
        )
      })).filter(proc => proc.manufacturers.length > 0)

      const defaultProcess = filtered.find(p => p.name === 'Injection Mold') ?? filtered[0] ?? null
      const defaultMfrs = defaultProcess ? defaultProcess.manufacturers : []
      set({
        processes: filtered,
        selectedProcess: defaultProcess?.name ?? null,
        manufacturers: defaultMfrs,
        selectedSeries: defaultMfrs.flatMap(m => m.series)
      })
    } catch (error) {
      set({ error: error.message })
    }
  },

  // Set selected process and update manufacturers
  setSelectedProcess: (processName) => {
    const processes = get().processes
    const selectedProcess = processes.find(p => p.name === processName)
    const manufacturers = selectedProcess ? selectedProcess.manufacturers : []
    set({
      selectedProcess: processName,
      manufacturers,
      selectedSeries: [],
      // Will be auto-selected by SearchForm's useEffect
    })
  },

  // Perform search
  search: async (length, width, depth) => {
    set({ loading: true, error: null, results: [] })
    try {
      const { selectedSeries, selectedProcess } = get()
      const { user, isAdmin } = (await import('./authStore')).useAuthStore.getState()
      const allowed = user?.allowedManufacturers || []
      const allowedManufacturers = (isAdmin || allowed.includes('*')) ? [] : allowed
      const response = await axios.post('/api/search', {
        length: parseFloat(length),
        width: parseFloat(width),
        depth: parseFloat(depth),
        process: selectedProcess,
        series: selectedSeries.length > 0 ? selectedSeries : [],
        allowedManufacturers,
        maxResults: 50
      })

      set({
        results: response.data.results,
        searchConfig: response.data.config || null,
        searchedDimensions: { length: parseFloat(length), width: parseFloat(width), depth: parseFloat(depth) },
        loading: false
      })

      // Save to history
      if (response.data.results.length > 0) {
        const { user } = (await import('./authStore')).useAuthStore.getState()
        if (user) {
          try {
            await axios.post('/api/auth/search-history', {
              uid: user.uid,
              length,
              width,
              depth,
              seriesFilter: selectedSeries,
              resultCount: response.data.results.length
            })
          } catch (err) {
            console.error('Failed to save search history:', err)
          }
        }
      }
    } catch (error) {
      set({ error: error.message, loading: false })
    }
  },

  // Toggle series selection
  toggleSeries: (series) => {
    const current = get().selectedSeries
    set({
      selectedSeries: current.includes(series)
        ? current.filter(s => s !== series)
        : [...current, series]
    })
  },

  // Set selected series
  setSelectedSeries: (series) => {
    set({ selectedSeries: series })
  },

  // Clear results and error only (preserves series selection)
  clearSearch: () => {
    set({ results: [], error: null })
  },

  // Clear everything including series and process selection
  clearResults: () => {
    set({ results: [], selectedProcess: null, manufacturers: [], selectedSeries: [] })
  },

  // Clear error
  clearError: () => set({ error: null })
}))
